import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Form } from 'antd';
import { StepThreeFormContext } from './StepThreeFormContext';
import {
  GenericDataFormContext,
  LOADING_SAVED_QUOTATIONS_STATES,
  QUOTATION_BUTTON,
  QuotationButton,
  QuotationInfoContext,
  QuotationStep,
  StepOneFormContext,
} from '../../../../shared';
import { StepTwoFormContext } from '../../StepTwo/contexts';
import {
  GetCommertialsConditionsResponse,
  QuotationCoverage,
} from '../../../../../../../../shared/interfaces';
import QuotationService, {
  DEFAULT_PAYMENT_PLAN_CODE,
} from '../../../../../../../../shared/services/quotation';
import {
  generateInputID,
  getCoverageAndActivityKeyByID,
} from '../../../../AP/Steps/StepThree/components/Quotation/utils';
import {
  PersonalEffectsQuotationComponent,
  PersonalEffectsQuotationRequest,
  WebPersonalEffectsQuotation,
} from '../../../../../../../../shared/interfaces/services/personal-effects-quotation-service';
import { getValueDefaultPaymentMethod } from '../../../../utils/get-value-default-payment-method';
import { PersonalEffectsQuotationFormByTab } from '../components';
import { WizardDataFormContext } from '../../../Wizard/contexts/WizardDataForm';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../shared/interfaces/loading-renewal-quotation-states-enum';
import { LoadQuotationToRenewResponse } from '../../../../shared/interfaces/load-renewal-quotation-response-interface';
import { scrollToEmitButton } from '../../../../../../../../shared/utils';

export interface CoveragePerPlanId {
  [key: string | number]: boolean;
}

export interface PersonalEffectsQuotationForm {
  [key: string]: string;
}

export interface PersonalEffectsPaymentMethodForm {
  [key: string]: string | number;
  paymentMethod: string;
  comission: number;
  extraVariable: number;
  variation: number;
}

export const StepThreeFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const [stepThreePersonalEffectsQuotationForm] =
    Form.useForm<PersonalEffectsQuotationForm>();
  const [personalEffectsPaymentMethodsForm] =
    Form.useForm<PersonalEffectsPaymentMethodForm>();

  const {
    quotationInfo,
    currentStep,
    loadSaveQuotationState,
    savedQuotation,
    quotationToRenewal,
    renewalQuotationState,
    setRenewalQuotationState,
    setLoadSaveQuotationState,
    setCurrentStep,
    setLoadingSavedQuotation,
    setLoadingRenewalQuotation
  } = useContext(QuotationInfoContext);

  const {
    allLocalitiesPerProvince,
    paymentMethodsList,
    allCoveragesPerPlan,
    setPlansSelectedByTabs,
    setCanShowSpinner,
  } = useContext(GenericDataFormContext);
  const { clientSelected } = useContext(StepOneFormContext);

  const { tabs, stepTwoForm } = useContext(StepTwoFormContext);

  const { handleTabErrors } = useContext(WizardDataFormContext);

  const [
    personalEffectsQuotationComponents,
    setPersonalEffectsQuotationComponents,
  ] = useState<Array<PersonalEffectsQuotationComponent>>([]);

  const [commertialConditions, setCommertialConditions] =
    useState<GetCommertialsConditionsResponse>();

  const [quotationTabs, setQuotationTabs] = useState<Array<any>>([]);

  const [quotationResponse, setQuotationResponse] =
    useState<WebPersonalEffectsQuotation>();
  const [labelButton, setLabelButton] = useState<QuotationButton>(
    quotationResponse ? QUOTATION_BUTTON.RECOTIZAR : QUOTATION_BUTTON.COTIZAR
  );

  const [canGetCoveragesPerPlanByTab, setCanGetCoveragesPerPlanByTab] =
    useState<CoveragePerPlanId>();

  const clearPrimaFieldsOnFormChange = useCallback(() => {
    const allFields = stepThreePersonalEffectsQuotationForm.getFieldsValue();
    const allPrimaKeys = Object.keys(allFields).filter((el) =>
      el.includes('prima')
    );
    stepThreePersonalEffectsQuotationForm.resetFields(allPrimaKeys);
  }, [stepThreePersonalEffectsQuotationForm]);

  const getCommertialsConditions = useCallback(async () => {
    try {
      const response = await QuotationService.getComertialsConditions(
        quotationInfo?.branch!
      );

      if (response.data) {
        setCommertialConditions(response.data);
      }
    } catch (error) {}
  }, [quotationInfo, setCommertialConditions]);

  const generateCoveragesInfo = useCallback(
    (tabKey: string): Array<QuotationCoverage> => {
      const quotationCoverages: Array<QuotationCoverage> = [];

      const allFields = stepThreePersonalEffectsQuotationForm.getFieldsValue();

      const allCheckboxFields = Object.keys(allFields).filter((el) =>
        el.includes('checkbox')
      );

      const allCheckboxFieldByTab = allCheckboxFields.filter((el) =>
        el.includes(`personal-effects-tab-${tabKey}`)
      );

      allCheckboxFieldByTab.forEach((el) => {
        if (stepThreePersonalEffectsQuotationForm.getFieldValue(el)) {
          const { id, activityKey } = getCoverageAndActivityKeyByID(el);

          const planSelected =
            stepThreePersonalEffectsQuotationForm.getFieldValue(
              `plan-tab-${tabKey}`
            );

          const allCoveragesByTab = allCoveragesPerPlan.find(
            (el) => String(el.plan as string) === String(planSelected)
          )?.coverages;

          const coverage = allCoveragesByTab?.find((el) => el.id === id);

          const sumaAseguradaInputName = generateInputID(id, activityKey);

          const primaInputName = generateInputID(id, activityKey, 'prima');

          quotationCoverages.push({
            cobertura: coverage?.codigo!,
            riesgo: coverage?.codigoRiesgo!,
            sumaAsegurada: stepThreePersonalEffectsQuotationForm.getFieldValue(
              sumaAseguradaInputName
            ),
            prima:
              stepThreePersonalEffectsQuotationForm.getFieldValue(
                primaInputName
              ) ?? '0',
            pormilaje: '0',
          });
        }
      });

      return quotationCoverages;
    },
    [stepThreePersonalEffectsQuotationForm, allCoveragesPerPlan]
  );

  const generatePersonalEffectsComponentsInfo =
    useCallback((): Array<PersonalEffectsQuotationComponent> => {
      let components: Array<PersonalEffectsQuotationComponent> = [];

      tabs.forEach(({ key }: any) => {
        const location: string = stepTwoForm?.getFieldValue(
          `location-tab-${key}`
        );
        const provinceCode: string = stepTwoForm?.getFieldValue(
          `province-tab-${key}`
        );

        const localitiesOfProvince = allLocalitiesPerProvince.find(
          (el) => el.provinceCode === provinceCode
        )?.localities;

        const riskZone =
          localitiesOfProvince?.find(
            (el) => `${el.sufijoCodigoPostal}${el.codigoPostal}` === location
          )?.zonaRiesgosVarios ?? '';

        const component: PersonalEffectsQuotationComponent = {
          nroComponente: key,
          codPlan: stepTwoForm?.getFieldValue(`plan-tab-${key}`) as number,
          tipoVivienda: 0, //* es 0 para polizas nuevas.
          caracteristicasBien: [], //* es vacío para Efectos Personales
          cantCaracteristicas: 0,
          sufijoCodigoPostal: location[0],
          codigoPostal: location.slice(1),
          zonaRiesgo: riskZone,
          buenResultado: 0, //* es 0 para polizas nuevas.
          cobertura: generateCoveragesInfo(key),
          psua: 0, //* es 0 para polizas nuevas.
          capTarifa: '0',
          capTarifaInc1: '',
          capTarifaInc2: '',
          clasificacionRiesgo: 'A',
        };

        components.push(component);
      });

      setPersonalEffectsQuotationComponents(components);

      return components;
    }, [
      tabs,
      allLocalitiesPerProvince,
      stepTwoForm,
      generateCoveragesInfo,
      setPersonalEffectsQuotationComponents,
    ]);

  const generatePersonalEffectsQuotationForm =
    useCallback((): PersonalEffectsQuotationRequest => {
      const components = generatePersonalEffectsComponentsInfo();
      const paymentMethodCode =
        personalEffectsPaymentMethodsForm.getFieldInstance('paymentMethod')
          ? personalEffectsPaymentMethodsForm?.getFieldValue('paymentMethod').split('-')[1]
          : getValueDefaultPaymentMethod(paymentMethodsList);
      const administrativeSurcharge =
        personalEffectsPaymentMethodsForm.getFieldInstance('extraVariable')
          ? personalEffectsPaymentMethodsForm?.getFieldValue('extraVariable')
          : commertialConditions?.porcentajeExtraPrimaVariable;

      return {
        token: quotationInfo?.token!,
        arse: quotationInfo?.arse!,
        rama: quotationInfo?.branch!,
        codigoTipoFormaPago: paymentMethodCode,
        cantComponentes: components.length,
        cliente: clientSelected!,
        componentes: components,
        recargoAdministrativo: administrativeSurcharge,
      };
    }, [
      quotationInfo,
      clientSelected,
      generatePersonalEffectsComponentsInfo,
      personalEffectsPaymentMethodsForm,
      commertialConditions,
      paymentMethodsList,
    ]);

  const createTabs = useCallback(() => {
    const newTabs = tabs?.map(({ key }: any) => ({
      children: <PersonalEffectsQuotationFormByTab tabKey={key} />,
      forceRender: true,
      closable: false,
      key,
      label: `Efectos Personales ${key}`,
    }));

    setQuotationTabs(newTabs);

    setPlansSelectedByTabs(tabs, stepTwoForm);
  }, [tabs, setQuotationTabs, setPlansSelectedByTabs, stepTwoForm]);

  const updateFieldsBeforeQuotation = useCallback(
    ({ componentes }: WebPersonalEffectsQuotation) => {
      const paymentMethodCode =
        personalEffectsPaymentMethodsForm.getFieldValue('paymentMethod');
      const defaultPaymentMethod = paymentMethodsList?.find(
        (el) => el.codigoFormaDePago === DEFAULT_PAYMENT_PLAN_CODE
      );

      if (
        paymentMethodCode &&
        paymentMethodsList?.find((el) => `${el.codigoFormaDePago}-${el.nroPlanDePago}` === paymentMethodCode)
      ) {
        personalEffectsPaymentMethodsForm.setFieldValue(
          'paymentMethod',
          paymentMethodCode
        );
      } else {
        personalEffectsPaymentMethodsForm.setFieldValue(
          'paymentMethod',
          `${defaultPaymentMethod?.codigoFormaDePago}-${defaultPaymentMethod?.nroPlanDePago}`
        );
      }

      if (
        !personalEffectsPaymentMethodsForm.getFieldValue('commission') &&
        !personalEffectsPaymentMethodsForm.getFieldValue('extraVariable')
      ) {
        personalEffectsPaymentMethodsForm.setFieldValue(
          'commission',
          Number(commertialConditions?.porcentajeDeComision)
        );
        personalEffectsPaymentMethodsForm.setFieldValue(
          'extraVariable',
          Number(commertialConditions?.porcentajeExtraPrimaVariable)
        );
      }

      componentes.forEach(({ nroDeComponente, coberturas }) => {
        coberturas.forEach(({ codigoCobertura, codigoRiesgo, prima }) => {
          const primaInputName = generateInputID(
            `${codigoCobertura}-${codigoRiesgo}`,
            `personal-effects-tab-${nroDeComponente}`,
            'prima'
          );
          stepThreePersonalEffectsQuotationForm.setFieldValue(
            primaInputName,
            Number(prima)
          );
        });
      });
    },
    [
      personalEffectsPaymentMethodsForm,
      paymentMethodsList,
      commertialConditions,
      stepThreePersonalEffectsQuotationForm,
    ]
  );

  const handlerOnClickQuotation = useCallback(
    async (form: PersonalEffectsQuotationRequest) => {
      setCanShowSpinner(true);

      try {
        await Promise.all([
          stepThreePersonalEffectsQuotationForm.validateFields(),
          personalEffectsPaymentMethodsForm.validateFields(),
        ]);

        const response =
          await QuotationService.generatePersonalEffectsQuotation(form);

        if (response.data) {
          scrollToEmitButton();
          setLabelButton(QUOTATION_BUTTON.EMITIR);

          //recover ID from original coverate and set it into response
          response.data.cotizacionWebRv.componentes.forEach(
            ({ nroDeComponente, coberturas }) => {
              const planSelected =
                stepThreePersonalEffectsQuotationForm.getFieldValue(
                  `plan-tab-${nroDeComponente}`
                );

              const coveragesPerPlan =
                allCoveragesPerPlan.find(
                  (el) => (el.plan as string) === String(planSelected)
                )?.coverages ?? [];

              coberturas?.forEach((coverage) => {
                const originalCoverage = coveragesPerPlan.find(
                  (item) =>
                    item.codigo === coverage.codigoCobertura &&
                    item.codigoRiesgo === coverage.codigoRiesgo
                );
                coverage.id = originalCoverage?.id;
                coverage.subCategoriaDeTextos = originalCoverage?.subCategoriaDeTextos;
              });
            }
          );

          setQuotationResponse(response.data.cotizacionWebRv);
          updateFieldsBeforeQuotation(response.data.cotizacionWebRv);
          return;
        }
      } catch (error: any) {
        if (error && error?.errorFields?.length > 0) {
          handleTabErrors(error?.errorFields, ['tab'], 'Efectos Personales');
        }
      } finally {
        setCanShowSpinner(false);
      }
    },
    [
      stepThreePersonalEffectsQuotationForm,
      setLabelButton,
      setCanShowSpinner,
      setQuotationResponse,
      updateFieldsBeforeQuotation,
    ]
  );

  useEffect(() => {
    if (currentStep === QuotationStep.THREE && !loadSaveQuotationState) {
      getCommertialsConditions();
    }
  }, [currentStep, loadSaveQuotationState, getCommertialsConditions]);

  const fillStepThreeServices = useCallback(async () => {
    await getCommertialsConditions();
  }, [getCommertialsConditions]);

  const fillStepThreeData = useCallback(async (isRenewal: boolean) => {
    let quotationToProccess: LoadQuotationToRenewResponse;
    if(isRenewal){
      quotationToProccess = quotationToRenewal;
    }else{
      quotationToProccess = savedQuotation;
    }
    const newTabs = tabs?.map(({ key }: any) => ({
      children: <PersonalEffectsQuotationFormByTab tabKey={key} />,
      forceRender: true,
      closable: false,
      key,
      label: `Efectos Personales ${key}`,
    }));

    setQuotationTabs(newTabs);
    
    //* Se setea vacio ya que no tenemos la primera respuesta de la COTIZACIÓN en un retome.
    setQuotationResponse({
      sumaAsegurada: '',
      premio: '',
      prima: '',
      componentes: quotationToProccess.bienes.map((bien: any) => {
        return {
          nroDeComponente: bien.numeroComponente,
          producto: '',
          tipoVivienda: '',
          codigoPostal: '',
          sufijoCodigoPostal: '',
          zonaRiesgo: '',
          buenResultado: '',
          sumaAsegurada: '',
          premio: '',
          requiereInspeccion: '',
          porcentajeSumaAsegurada: '',
          capituloTarifa: '',
          capituloTarifaInc1: '',
          agravamiento: '',
          caracteristicas: isRenewal ? [] : savedQuotation.caracteristicasSeleccionadas,
          coberturas: isRenewal ? bien.coberturasSeleccionadas : savedQuotation.coberturasSeleccionadas,
          impuestos: [
            {
              codigoCobertura: '',
              impComision: '',
              impExtraPrimaFija: '',
              impImpuestosInternos: '',
              impIva: '',
              impIvaPercepcion: '',
              impIvaRnl: '',
              impIibbPercepcion: '',
              impIibbRiesgo: '',
              impIibbEmpresa: '',
              porImpuestosInternos: '',
              porIva: '',
              porIvaRnl: '',
              porIvaPercepcion: '',
              porTasaSsn: '',
              porServiciosSociales: '',
              impExtraPrimaVar: '',
              impRecargoFinanciero: '',
              selladoDeEmpresa: '',
              selladoDelRiesgo: '',
              impServiciosSociales: '',
              impTasaSsn: '',
              porComision: '',
              porExtraPrimaVar: '',
              porRecargoFinanciero: '',
              calculoImpuestos: '',
            },
          ],
          bonificaciones: [],
        };
      }),
      impuestos: {
        rama: '1',
        cantPolizaXRama: '',
        prima: '',
        porRecargoFinanciero: '',
        impRecargoFinanciero: '',
        impExtraPrimaFija: '',
        impSubtotalPrima: '',
        selladoDelRiesgo: '',
        selladoDeEmpresa: '',
        porImpuestosInternos: '',
        impImpuestosInternos: '',
        porServiciosSociales: '',
        impServiciosSociales: '',
        porTasaSsn: '',
        impTasaSsn: '',
        impIibbPercepcion: '',
        impIibbEmpresa: '',
        impIibbRiesgo: '',
        impIvaRnl: '',
        porIvaRnl: '',
        impIva: '',
        porIva: '',
      },
      condicionesComerciales: {
        cantPolizaXRama: '',
        porRecargoAdminist: '',
        impRecargoAdminist: '',
        porComisionProd: '',
        impComision: '',
        porRecargoAdminist1: '',
        porComisionProd1: '',
      },
      condicionesComerciales1: {
        rama: '1',
        porcRecargoAdminist: '',
      },
    });

    quotationToProccess.bienes.forEach((bien: any, index: number) => {
      const _tab = index + 1;

      bien.coberturasSeleccionadas.forEach((cobertura: any) => {
        stepThreePersonalEffectsQuotationForm.setFields([
          {
            name: `checkbox_${cobertura.codigo}-${cobertura.codigoRiesgo}_personal-effects-tab-${_tab}`,
            value: true,
          },
          {
            name: `prima_${cobertura.codigo}-${cobertura.codigoRiesgo}_personal-effects-tab-${_tab}`,
            value: cobertura.prima,
          },
          {
            name: `sumaAsegurada_${cobertura.codigo}-${cobertura.codigoRiesgo}_personal-effects-tab-${_tab}`,
            value: cobertura.sumaAsegurada,
          },
        ]);
      });
    });

    personalEffectsPaymentMethodsForm.setFields([
      { name: `paymentMethod`, value: `${quotationToProccess.formaDePago.codigo}-${quotationToProccess.formaDePago.codigoDePlanDePagos}` },
      {
        name: `commission`,
        value: quotationToProccess.formaDePago.porcentajeComision,
      },
      {
        name: `extraVariable`,
        value: quotationToProccess.formaDePago.porcentajeExtraPrimaVariable,
      },
      {
        name: `variation`,
        value:
          Number(quotationToProccess.formaDePago.porcentajeComision) -
          commertialConditions!.porcentajeDeComision,
      },
    ]);
    setLabelButton(QUOTATION_BUTTON.RECOTIZAR);
  }, [
    tabs,
    savedQuotation,
    quotationToRenewal,
    setQuotationTabs,
    commertialConditions,
    stepThreePersonalEffectsQuotationForm,
    personalEffectsPaymentMethodsForm,
  ]);

  const handlerOnClickEmit = useCallback(async () => {
    setCurrentStep(QuotationStep.FOUR);
  }, [setCurrentStep]);

  const handlerOnClick = useCallback(async () => {
    
    try {
      const form: PersonalEffectsQuotationRequest =
      generatePersonalEffectsQuotationForm();
      if (labelButton === QUOTATION_BUTTON.EMITIR)
        return await handlerOnClickEmit();

      if (labelButton === QUOTATION_BUTTON.COTIZAR)
        return await handlerOnClickQuotation(form);

      if (labelButton === QUOTATION_BUTTON.RECOTIZAR)
        return await handlerOnClickQuotation(form);
    } catch (error) {}
  }, [
    labelButton,
    generatePersonalEffectsQuotationForm,
    handlerOnClickEmit,
    handlerOnClickQuotation,
  ]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING
      );
      fillStepThreeServices();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING &&
      commertialConditions
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED
      );
    }
  }, [loadSaveQuotationState, commertialConditions]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING
      );
      fillStepThreeData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING &&
      quotationTabs?.length === tabs?.length &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      commertialConditions &&
      allCoveragesPerPlan.length > 0
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED
      );
    }
  }, [
    loadSaveQuotationState,
    savedQuotation,
    quotationTabs,
    tabs,
    labelButton,
    commertialConditions,
    allCoveragesPerPlan,
  ]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING);
      setLoadingSavedQuotation(false);
      setCurrentStep(QuotationStep.THREE);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING
    ) {
      handlerOnClick();
    }
  }, [loadSaveQuotationState]);

  /**
   * Renewal
   */

  useEffect(() => {
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL
      );
      fillStepThreeServices();
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (
      renewalQuotationState ===
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL &&
      commertialConditions
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL
      );
    }
  }, [renewalQuotationState, commertialConditions]);

  useEffect(() => {
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL
      );
      fillStepThreeData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (
      renewalQuotationState ===
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL &&
      quotationTabs?.length === tabs?.length &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      commertialConditions &&
      allCoveragesPerPlan.length > 0
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL
      );
    }
  }, [
    renewalQuotationState,
    savedQuotation,
    quotationTabs,
    tabs,
    labelButton,
    commertialConditions,
    allCoveragesPerPlan,
  ]);

  useEffect(() => {
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL);
      setLoadingRenewalQuotation(false);
      setCurrentStep(QuotationStep.THREE);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (
      renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL
    ) {
      handlerOnClick();
    }
  }, [renewalQuotationState]);

  const values = useMemo(
    () => ({
      createTabs,
      quotationTabs,
      handlerOnClickQuotation,
      setQuotationTabs,
      stepThreePersonalEffectsQuotationForm,
      personalEffectsPaymentMethodsForm,
      labelButton,
      setLabelButton,
      quotationResponse,
      setQuotationResponse,
      commertialConditions,
      clearPrimaFieldsOnFormChange,
      generatePersonalEffectsQuotationForm,
      personalEffectsQuotationComponents,
      canGetCoveragesPerPlanByTab,
      setCanGetCoveragesPerPlanByTab,
      handlerOnClick,
    }),
    [
      createTabs,
      quotationTabs,
      setQuotationTabs,
      handlerOnClickQuotation,
      stepThreePersonalEffectsQuotationForm,
      personalEffectsPaymentMethodsForm,
      labelButton,
      setLabelButton,
      quotationResponse,
      setQuotationResponse,
      commertialConditions,
      clearPrimaFieldsOnFormChange,
      generatePersonalEffectsQuotationForm,
      personalEffectsQuotationComponents,
      canGetCoveragesPerPlanByTab,
      setCanGetCoveragesPerPlanByTab,
      handlerOnClick,
    ]
  );

  return (
    <StepThreeFormContext.Provider value={values}>
      {children}
    </StepThreeFormContext.Provider>
  );
};
